import { activityAPI } from '../services/api';

export interface ProjectActivityState {
  lastAcknowledgedAt: string | null;
  taskSeenAt: Record<string, string>;
}

const projectAckCache = new Map<string, string | null>();
const taskSeenCache = new Map<string, string>();

export function hydrateProjectActivity(projectId: string, state?: ProjectActivityState | null): void {
  if (!projectId || !state) return;
  projectAckCache.set(projectId, state.lastAcknowledgedAt);
  for (const [taskId, seenAt] of Object.entries(state.taskSeenAt || {})) {
    taskSeenCache.set(taskId, seenAt);
  }
}

export function hydrateActivityFromProjects(
  projects: Array<{ _id: string; userActivity?: ProjectActivityState }>,
): void {
  for (const project of projects) {
    if (project.userActivity) {
      hydrateProjectActivity(project._id, project.userActivity);
    }
  }
}

export async function fetchProjectActivity(projectId: string): Promise<ProjectActivityState> {
  const response = await activityAPI.getProject(projectId);
  const state = response.data as ProjectActivityState;
  hydrateProjectActivity(projectId, state);
  return state;
}

export function getCachedProjectAck(projectId: string): Date | null {
  if (!projectAckCache.has(projectId)) return null;
  const raw = projectAckCache.get(projectId);
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getCachedTaskSeenAt(taskId: string): Date | null {
  const raw = taskSeenCache.get(taskId);
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function setCachedProjectAck(projectId: string, at: Date): void {
  projectAckCache.set(projectId, at.toISOString());
}

export function setCachedTaskSeenAt(taskId: string, at: Date): void {
  taskSeenCache.set(taskId, at.toISOString());
}

export async function syncAcknowledgeProject(projectId: string): Promise<ProjectActivityState> {
  const response = await activityAPI.acknowledgeProject(projectId);
  const state = response.data as ProjectActivityState;
  hydrateProjectActivity(projectId, state);
  return state;
}

export async function syncTaskSeen(taskId: string): Promise<{
  projectId: string;
  state: ProjectActivityState;
  hasUnreadActivity: boolean;
}> {
  const response = await activityAPI.markTaskSeen(taskId);
  const data = response.data as ProjectActivityState & { projectId: string; hasUnreadActivity?: boolean };
  const { projectId, hasUnreadActivity, ...state } = data;
  hydrateProjectActivity(projectId, state);
  return {
    projectId,
    state,
    hasUnreadActivity: !!hasUnreadActivity,
  };
}

export function clearActivityCaches(): void {
  projectAckCache.clear();
  taskSeenCache.clear();
}
